import { BoxItem } from 'box-ui-elements/es';
import { FileType } from 'enums';
import { SubmissionMarket, UpdateSubmissionMarketRequest } from 'types';
import { CYBER_MARKETS } from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/markets/cyber-markets';
import {
  getDeclinedMarketNames,
  getQuotingMarketNames,
} from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/markets/utils';
import {
  createAnalyzeQuotesStep,
  createApiMarketingStep,
  createDataImportedFromAMSStep,
  createDeclinationStep,
  createEmailMarketingStep,
  createInformationSavedToAMSStep,
  createMarketingStrategyAnalysisStep,
  createPortalMarketingStep,
  createProposalCreationStep,
  createQuotesReceivedStep,
  createReviewProposal,
  DemoStep,
  FetchingAccountInformationStep,
  WritingInformationBackToAMS,
} from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/steps/shared-steps';
import { EmailListItemAttributes } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/Email/utils';

interface CyberDemoStepsParams {
  submissionId: string;
  markets: SubmissionMarket[];
  reFetchWorkspace: () => Promise<void>;
  updateSubmissionMarketRequest: (
    id: string,
    data: UpdateSubmissionMarketRequest,
    skipRefetch?: boolean,
  ) => Promise<void>;
  fetchFromAMS: (submissionId: string) => Promise<void | null>;
  marketViaAPI: (submissionId: string) => Promise<void | null>;
  marketViaEmail: (submissionId: string) => Promise<void | null>;
  marketViaPortal: (submissionId: string) => Promise<void | null>;
  receiveQuotes: (params: {
    submissionId: string;
    quoteType?: FileType.MarketQuote | FileType.RenewalPolicy;
  }) => Promise<void | null>;
  createProposal: (submissionId: string) => Promise<void | null>;
  writeToAMS: (submissionId: string) => Promise<void | null>;
  reFetchFileItems: () => Promise<void>;
  reFetchSubmissionEmails: () => Promise<void>;
  expiringPolicy?: BoxItem;
  application?: BoxItem;
  marketQuotes: Array<BoxItem>;
  proposalFile?: BoxItem;
  marketingEmails: EmailListItemAttributes[];
  onClose: () => void;
  navigateToProposal: () => void;
}

export function createCyberDemoSteps(params: CyberDemoStepsParams): DemoStep[] {
  const {
    submissionId,
    markets,
    reFetchWorkspace,
    updateSubmissionMarketRequest,
    fetchFromAMS,
    marketViaAPI,
    marketViaEmail,
    marketViaPortal,
    receiveQuotes,
    createProposal,
    reFetchFileItems,
    reFetchSubmissionEmails,
    expiringPolicy,
    application,
    marketQuotes,
    proposalFile,
    marketingEmails,
    onClose,
    navigateToProposal,
    writeToAMS,
  } = params;

  const declinedApiMarketNames = getDeclinedMarketNames(CYBER_MARKETS, 'API');
  const quotingMarketNames = getQuotingMarketNames(CYBER_MARKETS);

  return [
    FetchingAccountInformationStep,

    createDataImportedFromAMSStep({
      expiringPolicy,
      application,
      onClose,
      fetchFromAMS,
      reFetchFileItems,
      reFetchWorkspace,
      submissionId,
    }),

    createMarketingStrategyAnalysisStep({
      markets: CYBER_MARKETS,
    }),

    createApiMarketingStep({
      markets: CYBER_MARKETS,
      marketViaAPI,
      reFetchWorkspace,
      submissionId,
    }),

    createEmailMarketingStep({
      markets: CYBER_MARKETS,
      marketViaEmail,
      reFetchSubmissionEmails,
      reFetchWorkspace,
      onClose,
      marketingEmails,
      submissionId,
    }),

    createPortalMarketingStep({
      markets: CYBER_MARKETS,
      marketViaPortal,
      reFetchWorkspace,
      submissionId,
      videoUrl: 'https://s3.us-west-2.amazonaws.com/demo.dev.capitola-ins.com/chubb-portal-demo.mov',
    }),

    createDeclinationStep({
      declinedMarketNames: declinedApiMarketNames,
      markets,
      updateSubmissionMarketRequest,
    }),

    createQuotesReceivedStep({
      quotingMarketNames,
      marketQuotes,
      receiveQuotes,
      reFetchFileItems,
      reFetchWorkspace,
      onClose,
      submissionId,
    }),

    createAnalyzeQuotesStep({
      markets: CYBER_MARKETS,
      marketQuotes,
      onClose,
    }),

    createProposalCreationStep({
      createProposal,
      reFetchFileItems,
      reFetchWorkspace,
      submissionId,
      proposalFile,
      onClose,
    }),

    createReviewProposal({
      navigateToProposal,
    }),

    WritingInformationBackToAMS,

    createInformationSavedToAMSStep({
      writeToAMS,
      submissionId,
    }),
  ];
}
